import React, { useEffect, useState, useContext } from 'react';
import { Col, Row, Button, Table } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { BsPlus, BsPencil, BsFileText, BsFillGrid3X3GapFill, BsList, BsListColumns} from 'react-icons/bs';
import { toast } from 'react-toastify';
import Breadcrumbs from '../../components/layout/breadcrumbs';
import Page from '../../components/page';
import { api } from '../../providers/apiClient';
import { ContextUser } from '../../providers/ContextUser';
import SkeletonLoading from '../../components/skeleton-loading';
import NoResults from '../../components/no-result';
import { IncidentCard, IncidentFilters } from './components';
import { IncidentCardsContainer, StatsContainer, CustomButton, ListActionButton, ListBadgesStyles } from './styles';
import StatusCounters from '../../components/stats/incident-status-counters';
import { buildOrgLevelBreadcrumbs } from '../../utils/breadcrumbUtils';
import { useConfirmToast } from '../../hooks/useConfirmToast';
import { generateIncidentReport } from '../../utils/incidentReport';
import { formatIncidentCode } from '../../utils/formatCodes';
import { LuAlertTriangle } from 'react-icons/lu';

const PageIncidents = ({ isCollapsed }) => {
  const navigate = useNavigate();
  const { orgId } = useParams();
  const { handleUpdateCurrentOrganization, currentOrganization } = useContext(ContextUser);
  const { confirm } = useConfirmToast();
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [isFetching, setIsFetching] = useState(true);
  const [incidents, setIncidents] = useState([]);
  const [filteredIncidents, setFilteredIncidents] = useState([]);
  const [stats, setStats] = useState({});
  const [currentOrgId, setCurrentOrgId] = useState(null);
  const [generatingReports, setGeneratingReports] = useState({});
  const [filters, setFilters] = useState({
    status: '',
    incidentType: '',
    severityLevel: '',
    startDate: '',
    endDate: '',
    search: ''
  });
  const [viewMode, setViewMode] = useState(() => {
    try {
      const saved = localStorage.getItem('incidentsViewMode');
      return saved === 'list' || saved === 'grid' ? saved : 'grid';
    } catch (e) {
      return 'grid';
    }
  }); // 'grid' | 'list'

  const setAndPersistViewMode = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem('incidentsViewMode', mode);
    } catch (e) {
      // ignore storage errors
    }
  };

  const incidentTypes = [
    { value: '', label: 'Todos os tipos' },
    { value: 'VAZAMENTO_DADOS', label: 'Vazamento de Dados' },
    { value: 'ACESSO_NAO_AUTORIZADO', label: 'Acesso Não Autorizado' },
    { value: 'PERDA_DADOS', label: 'Perda de Dados' },
    { value: 'ALTERACAO_NAO_AUTORIZADA', label: 'Alteração Não Autorizada' },
    { value: 'DESTRUICAO_DADOS', label: 'Destruição de Dados' },
    { value: 'OUTROS', label: 'Outros' }
  ];

  const severityLevels = [
    { value: '', label: 'Todas as severidades' },
    { value: 'BAIXA', label: 'Baixa' },
    { value: 'MEDIA', label: 'Média' },
    { value: 'ALTA', label: 'Alta' },
    { value: 'CRITICA', label: 'Crítica' }
  ];

  const statusOptions = [
    { value: '', label: 'Todos os status' },
    { value: 'ABERTO', label: 'Aberto' },
    { value: 'EM_ANALISE', label: 'Em Análise' },
    { value: 'CONCLUIDO', label: 'Concluído' },
    { value: 'ARQUIVADO', label: 'Arquivado' }
  ];

  // Carrega dados da organização e incidentes
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsFetching(true);
        
        // Se temos orgId na URL, usar ele diretamente
        let targetOrgId = orgId;
        
        // Se não temos orgId na URL, tentar obter da organização atual
        if (!targetOrgId && currentOrganization?.id) {
          targetOrgId = currentOrganization.id;
        }
        
        if (!targetOrgId) {
          console.error('ID da organização não encontrado');
          return;
        }
        
        // Buscar dados da organização para obter informações completas
        const orgResponse = await api.get(`/orgs/${targetOrgId}/branchs`);
        const orgData = orgResponse.data;
        
        // Atualizar organização atual com dados completos
        handleUpdateCurrentOrganization(orgData.organization);
        setCurrentOrgId(targetOrgId);
        
        // Buscar incidentes da organização
        const incidentsResponse = await api.get('/incidents', {
          params: {
            orgId: targetOrgId,
          },
        });
        const incidentsData = incidentsResponse.data.incidents || incidentsResponse.data;
        
        setIncidents(incidentsData);
        setFilteredIncidents(incidentsData);
        
        // Calcular estatísticas
        const statsData = calculateStats(incidentsData);
        setStats(statsData);
        
        // Configurar breadcrumbs usando a função utilitária
        const breadcrumbs = buildOrgLevelBreadcrumbs(
          orgData.organization,
          'Gestão de Incidentes',
          `/prefeitura/${targetOrgId}/incidents`
        );
        setBreadcrumbs(breadcrumbs);
        
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      } finally {
        setIsFetching(false);
      }
    };

    loadData();
  }, [orgId]); // Dependência apenas do orgId da URL

  // Aplica filtros quando mudarem
  useEffect(() => {
    applyFilters();
  }, [filters, incidents]);

  const calculateStats = (incidentsData) => {
    const total = incidentsData.length;
    const byStatus = incidentsData.reduce((acc, incident) => {
      acc[incident.status] = (acc[incident.status] || 0) + 1;
      return acc;
    }, {});
    
    const bySeverity = incidentsData.reduce((acc, incident) => {
      acc[incident.severityLevel] = (acc[incident.severityLevel] || 0) + 1;
      return acc;
    }, {});

    return {
      total,
      byStatus,
      bySeverity,
      open: byStatus.ABERTO || 0,
      inProgress: byStatus.EM_ANALISE || 0,
      completed: byStatus.CONCLUIDO || 0,
      archived: byStatus.ARQUIVADO || 0,
      critical: bySeverity.CRITICA || 0,
      high: bySeverity.ALTA || 0
    };
  };

  const applyFilters = () => {
    let filtered = [...incidents];

    // Filtro por texto
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filtered = filtered.filter(incident =>
        incident.title.toLowerCase().includes(searchTerm) ||
        formatIncidentCode(incident.id).toLowerCase().includes(searchTerm) ||
        incident.description.toLowerCase().includes(searchTerm)
      );
    }

    // Filtro por status
    if (filters.status) {
      filtered = filtered.filter(incident => incident.status === filters.status);
    }

    // Filtro por tipo
    if (filters.incidentType) {
      filtered = filtered.filter(incident => incident.incidentType === filters.incidentType);
    }

    // Filtro por severidade
    if (filters.severityLevel) {
      filtered = filtered.filter(incident => incident.severityLevel === filters.severityLevel);
    }

    // Filtro por data
    if (filters.startDate) {
      filtered = filtered.filter(incident => 
        new Date(incident.incidentDate) >= new Date(filters.startDate)
      );
    }

    if (filters.endDate) {
      filtered = filtered.filter(incident => 
        new Date(incident.incidentDate) <= new Date(filters.endDate)
      );
    }

    setFilteredIncidents(filtered);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };


  const handleCreateIncident = () => {
    navigate(`/prefeitura/${currentOrgId}/incidents/create`);
  };

  const handleViewIncident = (incidentId) => {
    navigate(`/prefeitura/${currentOrgId}/incidents/${incidentId}`);
  };

  const handleEditIncident = (incidentId) => {
    navigate(`/prefeitura/${currentOrgId}/incidents/${incidentId}/edit`);
  };

  const handleGenerateReport = async (incidentId) => {
    try {
      // Ativar loading para este incidente específico
      setGeneratingReports(prev => ({ ...prev, [incidentId]: true }));
      
      // Buscar dados completos do incidente
      const response = await api.get(`/incidents/${incidentId}`);
      const incident = response.data;
      
      // Gerar relatório
      await generateIncidentReport(incident, api);
      
      toast.success('Relatório gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      toast.error(`Erro ao gerar relatório: ${error.message}`);
    } finally {
      // Desativar loading para este incidente específico
      setGeneratingReports(prev => ({ ...prev, [incidentId]: false }));
    }
  };

  // Removido: lógica de confirmação/arquivamento local

  const getSeverityColor = (severity) => {
    const colors = {
      BAIXA: 'success',
      MEDIA: 'warning',
      ALTA: 'danger',
      CRITICA: 'dark'
    };
    return colors[severity] || 'secondary';
  };

  const getStatusColor = (status) => {
    const colors = {
      ABERTO: 'danger',
      EM_ANALISE: 'warning',
      CONCLUIDO: 'success',
      ARQUIVADO: 'dark'
    };
    return colors[status] || 'secondary';
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  // Botão de ação para o header
  const ButtonNewIncident = (
    <CustomButton
      key='btnNewIncident'
      onClick={handleCreateIncident}
      type='button'
      style={{
        display: 'flex',
        alignItems: 'center',
        zIndex: '99',
        fontSize: 13,
      }}
      id='create-incident-btn'
    >
      <BsPlus
        style={{
          fontSize: 20,
          marginRight: 10,
        }}
      />
      Novo Incidente
    </CustomButton>
  );

  const ToggleViewButtons = (
    <div key='toggleView' style={{ display: 'flex', gap: 12 }} id='incident-view-toggle'>
      <Button
        variant='link'
        size='sm'
        onClick={() => setAndPersistViewMode('grid')}
        id='toggle-grid-view'
        title='Visualização de incidentes em tabela'
        style={{
          display: 'flex',
          alignItems: 'center',
          color: viewMode === 'grid' ? '#0d6efd' : '#6b7280',
          textDecoration: 'none',
          padding: 0
        }}
      >
        <BsFillGrid3X3GapFill style={{ marginRight: 6 }} /> Tabela
      </Button>
      <Button
        variant='link'
        size='sm'
        onClick={() => setAndPersistViewMode('list')}
        id='toggle-list-view'
        title='Visualização de incidentes em lista'
        style={{
          display: 'flex',
          alignItems: 'center',
          color: viewMode === 'list' ? '#0d6efd' : '#6b7280',
          textDecoration: 'none',
          padding: 0
        }}
      >
        <BsList style={{ marginRight: 6 }} /> Lista
      </Button>
    </div>
  );

  return (
    <>
      <Breadcrumbs id='breadcrumbs-incidents' path={breadcrumbs} />
      <Page id='page-incidents' title='Gestão de Incidentes LGPD' description="Controle de ocorrências relacionadas à LGPD, voltado para uso do DPO e equipe responsável." icon={<LuAlertTriangle id='icon-incidents-page' />} sideAction={[ButtonNewIncident]}>
        
        {/* Contadores de Status dos Incidentes */}
        <StatsContainer id='incident-stats'>
          <StatusCounters stats={stats} />
        </StatsContainer>

        {/* Filtros */}
        <IncidentFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          incidentTypes={incidentTypes}
          severityLevels={severityLevels}
          statusOptions={statusOptions}
        />

        {/* Toggle de visualização após os filtros */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {ToggleViewButtons}
        </div>

        {/* Lista de Incidentes */}
        {isFetching ? (
          <Row id='incidents-skeleton'>
            {[0, 1, 2].map((item) => (
              <Col key={item} md={isCollapsed ? 6 : 4} className='mb-4'>
                <SkeletonLoading id={`skeleton-${item}`} height='300px' />
              </Col>
            ))}
          </Row>
        ) : filteredIncidents.length > 0 ? (
          viewMode === 'grid' ? (
            <IncidentCardsContainer id='incident-cards'>
              <Row>
                {filteredIncidents.map((incident) => (
                  <Col
                    key={incident.id}
                    md={isCollapsed ? 6 : 4}
                    className='mb-4'
                  >
                    <IncidentCard
                      incident={incident}
                      onView={handleViewIncident}
                      onEdit={handleEditIncident}
                      onReport={handleGenerateReport}
                      isCollapsed={isCollapsed}
                      isGeneratingReport={generatingReports[incident.id] || false}
                    />
                  </Col>
                ))}
              </Row>
            </IncidentCardsContainer>
          ) : (
            <div className='table-responsive' id='incident-list-table'>
              <ListBadgesStyles>
              <Table striped hover>
                <thead>
                  <tr>
                    <th>Número</th>
                    <th>Título</th>
                    <th>Data</th>
                    <th>Status</th>
                    <th>Severidade</th>
                    <th>DPO</th>
                    <th>Titulares Afetados</th>
                    <th className='text-end'></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredIncidents.map((incident) => (
                    <tr key={incident.id} id={`incident-row-${incident.id}`}>
                      <td>{formatIncidentCode(incident.id)}</td>
                      <td>{incident.title}</td>
                      <td>{new Date(incident.incidentDate).toLocaleDateString('pt-BR')}</td>
                      <td><span className={`badge badge-status ${incident.status}`}>{incident.status}</span></td>
                      <td><span className={`badge badge-severity ${incident.severityLevel}`}>{incident.severityLevel}</span></td>
                      <td>{incident.dpo?.name || '—'}</td>
                      <td>{incident.dataSubjectsCount || 0}</td>
                      <td className='text-end'>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                          <ListActionButton
                            className='action-button secondary'
                            onClick={() => handleViewIncident(incident.id)}
                            id={`list-view-incident-${incident.id}`}
                            title='Detalhes do incidente'
                          >
                            <BsListColumns />
                            Detalhes
                          </ListActionButton>
                          <ListActionButton
                            className='action-button secondary'
                            onClick={() => handleEditIncident(incident.id)}
                            id={`list-edit-incident-${incident.id}`}
                            title='Editar incidente'
                          >
                            <BsPencil />
                            Editar
                          </ListActionButton>
                          
                          <ListActionButton
                            className='action-button info'
                            onClick={() => handleGenerateReport(incident.id)}
                            id={`list-report-incident-${incident.id}`}
                            title='Gerar relatório PDF'
                            disabled={generatingReports[incident.id] || false}
                            style={{ opacity: generatingReports[incident.id] ? 0.7 : 1 }}
                          >
                            <BsFileText />
                            Relatório
                          </ListActionButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              </ListBadgesStyles>
            </div>
          )
        ) : (
          <NoResults
            id='no-incidents'
            message='Nenhum incidente encontrado'
            searchText={filters.search}
          />
        )}
      </Page>
    </>
  );
};

export default PageIncidents;
